import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal, Radio, Select, message } from 'antd';
import { reviewChangeAnswer } from '../../services/api';
import { getApiError } from '../../utils/apiError';
import { Question } from './checklistTypes';

const LEVELS = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'MAJOR', label: 'Major' },
  { value: 'MINOR', label: 'Minor' },
  { value: 'CONCERN', label: 'Concern' },
];

interface Props {
  visitId: string;
  question: Question | null;
  onClose: () => void;
  onDone: () => void;
}

const ReviewChangeModal: React.FC<Props> = ({ visitId, question, onClose, onDone }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const newAnswer = Form.useWatch('new_answer', form);

  useEffect(() => {
    if (question) form.setFieldsValue({
      new_answer: question.answer, new_level: question.nc?.level, reason: '',
    });
  }, [question, form]);

  const save = async () => {
    if (!question) return;
    const values = await form.validateFields();
    const data: any = { reason: values.reason };
    if (values.new_answer !== question.answer) data.new_answer = values.new_answer;
    if (values.new_answer === 'NO' && values.new_level !== question.nc?.level) data.new_level = values.new_level;
    setSaving(true);
    try {
      await reviewChangeAnswer(visitId, question.id, data);
      message.success('Change recorded; it will be listed in the report.');
      onDone();
    } catch (err) {
      message.error(getApiError(err, 'Could not record the change.'));
    } finally {
      setSaving(false);
    }
  };

  const wasNo = question?.answer === 'NO';
  const options = [
    ...(wasNo ? [{ value: 'NO', label: 'No' }] : []),
    { value: 'YES', label: 'Yes' }, { value: 'NA', label: 'N/A' },
  ];

  return (
    <Modal
      title={question ? `Change ${question.code}` : ''}
      open={!!question} onCancel={onClose} onOk={save} okText="Record change"
      confirmLoading={saving} destroyOnHidden
    >
      {question && <Alert type="info" showIcon message={question.text} style={{ marginBottom: 16 }} />}
      <Alert
        type="warning" showIcon style={{ marginBottom: 16 }}
        message="Every change needs a written reason and is listed in the report. Changing a No to Yes or N/A withdraws its NC."
      />
      <Form form={form} layout="vertical">
        <Form.Item name="new_answer" label="Answer">
          <Radio.Group optionType="button" buttonStyle="solid" options={options} />
        </Form.Item>
        {wasNo && newAnswer === 'NO' && (
          <Form.Item name="new_level" label="NC level"><Select options={LEVELS} /></Form.Item>
        )}
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Write the reason' }]}>
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReviewChangeModal;
